import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { 
  Package, 
  SendHorizontal, 
  LayoutDashboard, 
  Users, 
  AlertTriangle, 
  CheckCircle, 
  PackageOpen, 
  Link as LinkIcon, 
  ShoppingCart, 
  LogOut, 
  Truck, 
  MapPin, 
  Calendar, 
  Clock, 
  ChevronDown, 
  ClipboardCheck, 
  ArrowRight, 
  Loader2,
  RefreshCw,
  X
} from 'lucide-react';
import Products from './Products';
import OrderForm from './OrderForm';
import Customers from './Customers';
import Orders from './Orders';
import Fulfillment from './Fulfillment';
import UsersPage from './Users';
import DashboardHome from './DashboardHome';
import { fetchFulfillmentOrders, fetchDeliveryOrders, fetchDashboardStats } from '../lib/api';
import { isAdmin } from '../lib/auth';
import type { Order } from '../lib/types';

interface DashboardProps {
  onLogout: () => void;
}

interface DeliveryStats {
  pendingDeliveries: number;
  completedToday: number;
  scheduledTomorrow: number;
}

interface DashboardStats {
  totalProducts: number;
  lowStockProducts: number;
  totalQoh: number;
  activeOrderLinks: number;
}

const Dashboard: React.FC<DashboardProps> = ({ onLogout }) => {
  const location = useLocation();
  
  const [stats, setStats] = useState<DashboardStats>({
    totalProducts: 0,
    lowStockProducts: 0,
    totalQoh: 0,
    activeOrderLinks: 0
  });

  const [deliveryStats, setDeliveryStats] = useState<DeliveryStats>({
    pendingDeliveries: 0,
    completedToday: 0,
    scheduledTomorrow: 0
  });

  const [fulfillmentQueue, setFulfillmentQueue] = useState<Order[]>([]);
  const [loadingFulfillment, setLoadingFulfillment] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [deliveryOrders, setDeliveryOrders] = useState<{
    pendingDeliveries: Order[];
    completedToday: number;
    scheduledTomorrow: number;
  }>({
    pendingDeliveries: [],
    completedToday: 0,
    scheduledTomorrow: 0
  });
  const [loadingDeliveries, setLoadingDeliveries] = useState(true);
  const [deliveryError, setDeliveryError] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  useEffect(() => {
    loadFulfillmentQueue();
    loadDeliveryOrders();
    loadDashboardStats();
  }, []);

  const handleRefresh = async () => {
    if (refreshing) return;
    
    setRefreshing(true);
    try {
      await Promise.all([
        loadFulfillmentQueue(),
        loadDeliveryOrders(),
        loadDashboardStats()
      ]);
    } finally {
      setRefreshing(false);
    }
  };

  const loadDashboardStats = async () => {
    try {
      setLoadingStats(true);
      const data = await fetchDashboardStats();
      setStats(data);
      setError(null);
    } catch (err) {
      console.error('Error loading dashboard stats:', err);
      setError('Failed to load dashboard statistics');
    } finally {
      setLoadingStats(false);
    }
  };

  const loadFulfillmentQueue = async () => {
    try {
      setLoadingFulfillment(true);
      const orders = await fetchFulfillmentOrders();
      setFulfillmentQueue(orders);
      setError(null);
    } catch (err) {
      console.error('Error loading fulfillment queue:', err);
      setError('Failed to load fulfillment queue');
    } finally {
      setLoadingFulfillment(false);
    }
  };

  const loadDeliveryOrders = async () => {
    try {
      setLoadingDeliveries(true);
      const data = await fetchDeliveryOrders();
      setDeliveryOrders(data);
      setDeliveryError(null);
    } catch (err) {
      console.error('Error loading delivery orders:', err);
      setDeliveryError('Failed to load delivery orders');
    } finally {
      setLoadingDeliveries(false);
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/dashboard/customers', icon: Users, label: 'Customers' },
    { path: '/dashboard/products', icon: Package, label: 'Products' },
    { path: '/dashboard/order-form', icon: SendHorizontal, label: 'Send Order Form' },
    { path: '/dashboard/orders', icon: ShoppingCart, label: 'Orders' },
    { path: '/dashboard/fulfillment', icon: ClipboardCheck, label: 'Fulfillment' }
  ];

  // Add Users page for admin users
  if (isAdmin()) {
    navItems.push({ path: '/dashboard/users', icon: Users, label: 'Users' });
  }

  return (
    <div className="min-h-screen flex">
      {/* Fixed Sidebar */}
      <nav className="fixed top-0 left-0 h-screen w-64 bg-black shadow-lg flex flex-col z-50">
        <div className="p-6 border-b border-gray-800">
          <img
            src="https://wholesale-images.stickysaguaro.com/products/StickyLogoWHRStacked.png"
            alt="Sticky Saguaro Logo"
            className="h-16 mx-auto"
          />
        </div>
        <div className="flex-1 flex flex-col justify-between p-4 overflow-y-auto">
          <div className="space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                  isActive(item.path)
                    ? 'bg-green-600 text-white'
                    : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            ))}
          </div>
          <button
            onClick={onLogout}
            className="flex items-center space-x-3 p-3 rounded-lg text-red-500 hover:bg-red-500/10 transition-colors mt-4 w-full"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </nav>

      {/* Main Content Area */}
      <div className="flex-1 ml-64 min-h-screen flex flex-col bg-gray-50">
        <main className="flex-1 p-8">
          <Routes>
            <Route path="/" element={<DashboardHome />} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/products" element={<Products />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/fulfillment" element={<Fulfillment />} />
            <Route path="/order-form" element={<OrderForm />} />
            {isAdmin() && <Route path="/users" element={<UsersPage />} />}
          </Routes>
        </main>

        <footer className="bg-black py-4 px-8 text-center">
          <div className="text-gray-400">
            WASP Wholesale Manager V2 © 2025-{new Date().getFullYear()} - Sticky Saguaro
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Dashboard;